import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, ArrowRight, CheckCircle2, X, Loader2 } from 'lucide-react';
declare global {
  interface Window {
    hcaptcha?: {
      reset: (widgetId?: string) => void;
      render: (container: HTMLElement | string, params: Record<string, unknown>) => string;
    };
  }
}
export const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // Load Web3Forms client script — it auto-renders hCaptcha for [data-captcha="true"]
  // Per https://docs.web3forms.com/getting-started/customizations/spam-protection/hcaptcha
  useEffect(() => {
    if (document.querySelector('script[src*="web3forms.com/client/script.js"]')) {
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://web3forms.com/client/script.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);
  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');
    const form = event.currentTarget;
    const formData = new FormData(form);
    // Verify hCaptcha was completed
    const captchaToken = formData.get('h-captcha-response');
    if (!captchaToken || typeof captchaToken !== 'string' || !captchaToken.trim()) {
      setErrorMessage('Please complete the hCaptcha before sending.');
      return;
    }
    setIsSubmitting(true);
    // Build JSON payload per Web3Forms docs
    const payload: Record<string, string> = {
      access_key: '0b41ac13-ec8b-408e-8836-6689e10506ea',
      subject: 'New UHD Contact Form Submission'
    };
    formData.forEach((value, key) => {
      if (typeof value === 'string') payload[key] = value;
    });
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        setShowSuccess(true);
        form.reset();
        if (window.hcaptcha) {
          try {
            window.hcaptcha.reset();
          } catch {
            // no-op
          }
        }
      } else {
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
        if (window.hcaptcha) {
          try {
            window.hcaptcha.reset();
          } catch {
            // no-op
          }
        }
      }
    } catch (err) {
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="contact" className="py-24 bg-navy-800 relative border-t border-navy-700" data-id="element-19">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-20">
        <div className="bg-navy-900 border border-navy-700 rounded-sm overflow-hidden shadow-2xl" data-id="element-21">
          <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-22">
            {/* Left side - Info */}
            <div className="p-10 md:p-16 bg-gradient-to-br from-navy-900 to-navy-800 relative overflow-hidden" data-id="element-23">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-ice-500 to-gold-500" data-id="element-24"></div>

              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="text-3xl md:text-4xl font-bold text-white mb-4 uppercase tracking-tight" data-id="element-25">
                Ready to Take the{' '}
                <span className="text-gold-500" data-id="element-26">Next Step?</span>
              </motion.h2>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.1
            }} className="text-slate-300 mb-10 leading-relaxed" data-id="element-27">
                UHD wants to be known as the developmental and representation
                team that doesn't waste players' and families' time. We provide
                the tools you need to get to the next level.
              </motion.p>

              <div className="space-y-6" data-id="element-28">
                <motion.div initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.2
              }} className="flex items-center gap-4 text-white" data-id="element-29">
                  <div className="w-12 h-12 bg-navy-800 flex items-center justify-center rounded-sm border border-navy-700" data-id="element-30">
                    <Mail className="text-ice-500 w-5 h-5" data-id="element-31" />
                  </div>
                  <div data-id="element-32">
                    <p className="text-sm text-slate-400 font-semibold uppercase tracking-wider" data-id="element-33">
                      Email Us
                    </p>
                    <p className="font-medium" data-id="element-34">
                      contact@universalhockeydev.com
                    </p>
                  </div>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.3
              }} className="flex items-center gap-4 text-white" data-id="element-35">
                  <div className="w-12 h-12 bg-navy-800 flex items-center justify-center rounded-sm border border-navy-700" data-id="element-36">
                    <Phone className="text-ice-500 w-5 h-5" data-id="element-37" />
                  </div>
                  <div data-id="element-38">
                    <p className="text-sm text-slate-400 font-semibold uppercase tracking-wider" data-id="element-39">
                      Call Us
                    </p>
                    <p className="font-medium" data-id="element-40">Available upon request</p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Right side - Form */}
            <div className="p-10 md:p-16 bg-navy-800" data-id="element-41">
              <form className="space-y-6" onSubmit={onSubmit} data-id="element-42">
                {/* Honeypot field for spam protection */}
                <input type="checkbox" name="botcheck" className="hidden" style={{
                display: 'none'
              }} tabIndex={-1} autoComplete="off" data-id="element-43" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-44">
                  <div data-id="element-45">
                    <label htmlFor="firstName" className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider" data-id="element-46">
                      First Name
                    </label>
                    <input type="text" id="firstName" name="firstName" required disabled={isSubmitting} className="w-full bg-navy-900 border border-navy-700 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-ice-500 transition-colors disabled:opacity-60" placeholder="John" data-id="element-47" />
                  </div>
                  <div data-id="element-48">
                    <label htmlFor="lastName" className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider" data-id="element-49">
                      Last Name
                    </label>
                    <input type="text" id="lastName" name="lastName" required disabled={isSubmitting} className="w-full bg-navy-900 border border-navy-700 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-ice-500 transition-colors disabled:opacity-60" placeholder="Doe" data-id="element-50" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-51">
                  <div data-id="element-52">
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider" data-id="element-53">
                      Email Address
                    </label>
                    <input type="email" id="email" name="email" required disabled={isSubmitting} className="w-full bg-navy-900 border border-navy-700 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-ice-500 transition-colors disabled:opacity-60" placeholder="john@example.com" data-id="element-54" />
                  </div>
                  <div data-id="element-55">
                    <label htmlFor="phone" className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider" data-id="element-56">
                      Phone
                    </label>
                    <input type="tel" id="phone" name="phone" disabled={isSubmitting} className="w-full bg-navy-900 border border-navy-700 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-ice-500 transition-colors disabled:opacity-60" placeholder="(555) 123-4567" data-id="element-57" />
                  </div>
                </div>

                <div data-id="element-58">
                  <label htmlFor="level" className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider" data-id="element-59">
                    Current Level of Play
                  </label>
                  <select id="level" name="level" required disabled={isSubmitting} className="w-full bg-navy-900 border border-navy-700 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-ice-500 transition-colors appearance-none disabled:opacity-60" data-id="element-60">
                    <option value="" data-id="element-61">Select Level</option>
                    <option value="youth" data-id="element-62">Youth (U10-U18)</option>
                    <option value="junior" data-id="element-63">Junior</option>
                    <option value="college" data-id="element-64">College (NCAA/ACHA)</option>
                    <option value="pro" data-id="element-65">Professional</option>
                  </select>
                </div>

                <div data-id="element-66">
                  <label htmlFor="message" className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider" data-id="element-67">
                    Message
                  </label>
                  <textarea id="message" name="message" rows={4} required disabled={isSubmitting} className="w-full bg-navy-900 border border-navy-700 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-ice-500 transition-colors resize-none disabled:opacity-60" placeholder="How can we help you?" data-id="element-68"></textarea>
                </div>

                {/* hCaptcha — auto-rendered by Web3Forms client script via data-captcha="true" */}
                <div data-id="element-69">
                  <label className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider" data-id="element-70">
                    hCaptcha Verification
                  </label>
                  <div className="h-captcha" data-captcha="true" data-theme="dark" data-id="element-71"></div>
                  <p className="text-xs text-slate-400 mt-2" data-id="element-72">
                    Protected by hCaptcha — please complete to send your
                    message.
                  </p>
                </div>

                {errorMessage && <div role="alert" className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3 rounded-sm" data-id="element-73">
                    {errorMessage}
                  </div>}

                <button type="submit" disabled={isSubmitting} className="w-full bg-gold-500 hover:bg-gold-400 disabled:opacity-70 disabled:cursor-not-allowed text-navy-900 font-bold text-lg py-4 rounded-sm transition-colors flex items-center justify-center gap-2" data-id="element-74">
                  {isSubmitting ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-75" />
                      Sending...
                    </> : <>
                      Send Message
                      <ArrowRight className="w-5 h-5" data-id="element-76" />
                    </>}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <AnimatePresence data-id="element-77">
        {showSuccess && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/80 backdrop-blur-sm" onClick={() => setShowSuccess(false)} role="dialog" aria-modal="true" aria-labelledby="success-title" data-id="element-78">
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          type: 'spring',
          stiffness: 260,
          damping: 22
        }} onClick={e => e.stopPropagation()} className="relative bg-navy-800 border border-navy-700 rounded-sm shadow-2xl max-w-md w-full p-8 text-center" data-id="element-79">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-ice-500 to-gold-500" data-id="element-80"></div>

              <button type="button" onClick={() => setShowSuccess(false)} aria-label="Close" className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors" data-id="element-81">
                <X className="w-5 h-5" data-id="element-82" />
              </button>

              <div className="flex justify-center mb-5" data-id="element-83">
                <div className="w-16 h-16 rounded-full bg-gold-500/10 border border-gold-500/30 flex items-center justify-center" data-id="element-84">
                  <CheckCircle2 className="w-9 h-9 text-gold-500" data-id="element-85" />
                </div>
              </div>

              <h3 id="success-title" className="text-2xl font-bold text-white mb-3 uppercase tracking-tight" data-id="element-86">
                Message Sent
              </h3>
              <p className="text-slate-300 leading-relaxed mb-6" data-id="element-87">
                Your message has been sent to our staff. We'll be in touch with
                you shortly.
              </p>

              <button type="button" onClick={() => setShowSuccess(false)} className="w-full bg-gold-500 hover:bg-gold-400 text-navy-900 font-bold py-3 rounded-sm transition-colors" data-id="element-88">
                Close
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
};